import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Search,
  Users,
  Mail,
  Phone,
  Building2,
  Briefcase,
  UserCheck,
  UserX,
  Filter,
  LayoutGrid,
  LayoutList,
  ArrowUpDown,
  Eye,
  AlertCircle,
  Clock,
  CheckCircle2,
  MapPin,
  Calendar,
  TrendingUp,
} from 'lucide-react';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import { EmployeeImage } from '@/components/EmployeeImage';
import { TeamMemberDetailsDialog } from '@/components/TeamMemberDetailsDialog';
import { initializeMockData } from '@/data/mockData';
import type { Employee, WorkingPattern } from '@/types/timesheet';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useDataSource } from '@/contexts/DataSourceContext';
import { apiClient } from '@/services/apiClient';

// Extended Employee type with status
interface TeamMember extends Employee {
  status: 'active' | 'inactive' | 'on-leave';
  phone?: string;
  location?: string;
  startDate?: Date;
}

const getDemoPhotoUrl = (name: string, id: string): string => {
  const seed = encodeURIComponent(name.replace(/\s+/g, '-'));
  return `https://picsum.photos/seed/${seed}-${id}/300/300`;
};

export const TeamMembersPage: React.FC = () => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('team-members');
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiTeamMembers, setApiTeamMembers] = useState<TeamMember[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const initialMockTeamMembers: TeamMember[] = useMemo(() => {
    const mockData = initializeMockData();
    return mockData.employees.map((emp) => ({
      ...emp,
      status: Math.random() > 0.1 ? 'active' : 'inactive', // 90% active
      phone: `+44 ${Math.floor(Math.random() * 9000) + 1000} ${Math.floor(Math.random() * 900000) + 100000}`,
      location: ['London', 'Manchester', 'Birmingham', 'Leeds', 'Bristol', 'Remote'][Math.floor(Math.random() * 6)],
      startDate: new Date(2020 + Math.floor(Math.random() * 4), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1),
    }));
  }, []);

  const [mockTeamMembers, setMockTeamMembers] = useState<TeamMember[]>(initialMockTeamMembers);

  const teamMembers = screenMode === 'live' ? apiTeamMembers : mockTeamMembers;

  useEffect(() => {
    if (screenMode === 'live') {
      const fetchTeamMembers = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
          const employees = await apiClient.get<Employee[]>('/employees');
          
          const enhancedEmployees: TeamMember[] = employees.map((emp) => ({
            ...emp,
            photoUrl: getDemoPhotoUrl(emp.name, emp.id),
            status: emp.status || 'active',
            phone: emp.phone || '',
            location: emp.location || '',
            startDate: emp.startDate ? new Date(emp.startDate) : new Date(),
          }));
          
          setApiTeamMembers(enhancedEmployees);
        } catch (err) {
          console.error('Error fetching team members:', err);
          setError(err instanceof Error ? err.message : 'Failed to load team members');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchTeamMembers();
    }
  }, [screenMode]);

  // Get unique departments and roles for filters
  const departments = useMemo(() => {
    return Array.from(new Set(teamMembers.map(m => m.department))).sort();
  }, [teamMembers]);

  const roles = useMemo(() => {
    return Array.from(new Set(teamMembers.map(m => m.role))).sort();
  }, [teamMembers]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = teamMembers.length;
    const active = teamMembers.filter(m => m.status === 'active').length;
    const inactive = total - active;
    const departmentCounts = teamMembers.reduce((acc, m) => {
      acc[m.department] = (acc[m.department] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const largestDepartment = Object.entries(departmentCounts).sort((a, b) => b[1] - a[1])[0];

    return {
      total,
      active,
      inactive,
      largestDepartment: largestDepartment ? largestDepartment[0] : 'N/A',
    };
  }, [teamMembers]);

  // Handler for metric card clicks
  const handleMetricCardClick = (metric: 'total' | 'active' | 'inactive') => {
    const isActive = 
      (metric === 'total' && statusFilter === 'all') ||
      (metric === 'active' && statusFilter === 'active') ||
      (metric === 'inactive' && statusFilter === 'inactive');

    if (isActive) {
      setStatusFilter('all');
    } else {
      setStatusFilter(metric === 'total' ? 'all' : metric);
    }
  };

  const getActiveMetric = (): 'total' | 'active' | 'inactive' | null => {
    if (statusFilter === 'active') return 'active';
    if (statusFilter === 'inactive') return 'inactive';
    if (statusFilter === 'all') return 'total';
    return null;
  };

  const activeMetric = getActiveMetric();

  // Filter and sort team members
  const filteredMembers = useMemo(() => {
    let filtered = teamMembers.filter(member => {
      // Search filter
      const matchesSearch = searchQuery === '' || 
        member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.department.toLowerCase().includes(searchQuery.toLowerCase());

      // Department filter
      const matchesDepartment = departmentFilter === 'all' || member.department === departmentFilter;

      // Role filter
      const matchesRole = roleFilter === 'all' || member.role === roleFilter;

      // Status filter
      const matchesStatus = statusFilter === 'all' || member.status === statusFilter;

      return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'department':
          return a.department.localeCompare(b.department);
        case 'role':
          return a.role.localeCompare(b.role);
        case 'email':
          return a.email.localeCompare(b.email);
        default:
          return 0;
      }
    });

    return filtered;
  }, [teamMembers, searchQuery, departmentFilter, roleFilter, statusFilter, sortBy]);

  const handleViewDetails = (member: TeamMember) => {
    setSelectedMember(member);
    setShowDetailsDialog(true);
  };

  const handleMemberUpdate = (updatedMember: TeamMember) => {
    // Update the member in the appropriate state based on screen mode
    if (screenMode === 'live') {
      setApiTeamMembers(prev => 
        prev.map(m => m.id === updatedMember.id ? updatedMember : m)
      );
    } else {
      // Update mock team members state
      setMockTeamMembers(prev => 
        prev.map(m => m.id === updatedMember.id ? updatedMember : m)
      );
    }
    // Update selected member to reflect changes in the dialog
    setSelectedMember(updatedMember);
  };

  const getStatusColor = (status: 'active' | 'inactive' | 'on-leave') => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20';
      case 'on-leave':
        return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20';
      default:
        return 'bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };



  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div>
          <Skeleton className="h-10 w-72 mb-3" />
          <Skeleton className="h-5 w-96" />
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <Skeleton key={i} className="h-80" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] space-y-4 p-6">
        <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-4">
          <AlertCircle className="h-10 w-10 text-red-600 dark:text-red-400" />
        </div>
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold">Failed to load team members</h3>
          <p className="text-sm text-muted-foreground max-w-md">{error}</p>
        </div>
        <Button onClick={() => window.location.reload()} size="lg">
          Try Again
        </Button>
      </div>
    );
  }

  // Empty state
  if (filteredMembers.length === 0 && searchQuery === '' && departmentFilter === 'all' && roleFilter === 'all' && statusFilter === 'all') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] space-y-4 p-6">
        <div className="rounded-full bg-muted p-4">
          <Users className="h-10 w-10 text-muted-foreground" />
        </div>
        <div className="text-center space-y-2">
          <h3 className="text-xl font-semibold">No team members found</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            There are no team members in the system yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      {/* Modern Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg">
            <Users className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-gray-900 to-gray-600 dark:from-gray-100 dark:to-gray-400 bg-clip-text text-transparent">
              Team Members
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage employee information and rate cards
            </p>
          </div>
        </div>
      </div>

      {/* Modern Stats Cards - Compact & Clean */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Members */}
        <Card 
          className={cn(
            "group relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30",
            activeMetric === 'total' && "ring-2 ring-blue-500 shadow-blue-200 dark:shadow-blue-900"
          )}
          onClick={() => handleMetricCardClick('total')}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500" />
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-400">
                Total Employees
              </CardTitle>
              <div className="p-2 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">{stats.total}</div>
            <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-1 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Across all departments
            </p>
          </CardContent>
        </Card>

        {/* Active Members */}
        <Card 
          className={cn(
            "group relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30",
            activeMetric === 'active' && "ring-2 ring-emerald-500 shadow-emerald-200 dark:shadow-emerald-900"
          )}
          onClick={() => handleMetricCardClick('active')}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500" />
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                Active
              </CardTitle>
              <div className="p-2 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                <UserCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-900 dark:text-emerald-100">{stats.active}</div>
            <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70 mt-1 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Currently active
            </p>
          </CardContent>
        </Card>

        {/* Inactive Members */}
        <Card 
          className={cn(
            "group relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/30 dark:to-gray-950/30",
            activeMetric === 'inactive' && "ring-2 ring-slate-500 shadow-slate-200 dark:shadow-slate-900"
          )}
          onClick={() => handleMetricCardClick('inactive')}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-slate-500/10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500" />
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-400">
                Inactive
              </CardTitle>
              <div className="p-2 rounded-lg bg-slate-500/10 group-hover:bg-slate-500/20 transition-colors">
                <UserX className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">{stats.inactive}</div>
            <p className="text-xs text-slate-600/70 dark:text-slate-400/70 mt-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Not currently active
            </p>
          </CardContent>
        </Card>

        {/* Largest Department */}
        <Card className="group relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30">
          <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500" />
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-400">
                Largest Department
              </CardTitle>
              <div className="p-2 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                <Building2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100 truncate">{stats.largestDepartment}</div>
            <p className="text-xs text-purple-600/70 dark:text-purple-400/70 mt-1 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Most team members
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Modern Search & Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or employee code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-11 border-0 bg-muted/50 focus-visible:ring-2"
                />
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-[160px] h-11 border-0 bg-muted/50">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[160px] h-11 border-0 bg-muted/50">
                  <Briefcase className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] h-11 border-0 bg-muted/50">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>

              {/* View Toggle */}
              <div className="flex border-0 rounded-lg bg-muted/50 p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="h-9"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-9"
                >
                  <LayoutList className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Info */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-muted-foreground font-medium">
          Showing <span className="text-foreground font-semibold">{filteredMembers.length}</span> of {teamMembers.length} employees
        </p>
        {(searchQuery || departmentFilter !== 'all' || roleFilter !== 'all' || statusFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchQuery('');
              setDepartmentFilter('all');
              setRoleFilter('all');
              setStatusFilter('all');
            }}
            className="h-8"
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* No results state */}
      {filteredMembers.length === 0 && (
        <Card className="border-0 shadow-md">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Filter className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No employees found</h3>
            <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
              Try adjusting your search or filter criteria to find what you're looking for
            </p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setDepartmentFilter('all');
                setRoleFilter('all');
                setStatusFilter('all');
              }}
            >
              Clear all filters
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Modern Team Members Grid */}
      {filteredMembers.length > 0 && (
        <>
          {viewMode === 'grid' ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {filteredMembers.map(member => (
                <Card 
                  key={member.id} 
                  className="group border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                  onClick={() => handleViewDetails(member)}
                >
                  {/* Employee Photo Header */}
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
                    <img 
                      src={member.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&size=400&background=random`}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Status Indicator */}
                    {member.status === 'active' && (
                      <div className="absolute top-3 right-3 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white shadow-lg" />
                    )}
                    {/* Employee Code Badge */}
                    <div className="absolute top-3 left-3 bg-slate-900/80 backdrop-blur-sm text-white px-2.5 py-1 rounded text-xs font-semibold">
                      {member.employeeCode}
                    </div>
                  </div>
                  
                  <CardContent className="p-5">
                    {/* Name & Role Section */}
                    <div className="mb-4">
                      <h3 className="font-semibold text-base mb-1 group-hover:text-blue-600 transition-colors">
                        {member.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">{member.role}</p>
                    </div>

                    {/* Info Grid */}
                    <div className="space-y-2.5 mb-4">
                      <div className="flex items-center gap-2.5 text-sm">
                        <div className="p-1.5 rounded-md bg-blue-50 dark:bg-blue-950/30">
                          <Briefcase className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="text-muted-foreground truncate">{member.role}</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-sm">
                        <div className="p-1.5 rounded-md bg-purple-50 dark:bg-purple-950/30">
                          <Building2 className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <span className="text-muted-foreground truncate">{member.department}</span>
                      </div>
                      <div className="flex items-center gap-2.5 text-sm">
                        <div className="p-1.5 rounded-md bg-indigo-50 dark:bg-indigo-950/30">
                          <Mail className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <span className="text-muted-foreground truncate text-xs">{member.email}</span>
                      </div>
                      {member.location && (
                        <div className="flex items-center gap-2.5 text-sm">
                          <div className="p-1.5 rounded-md bg-orange-50 dark:bg-orange-950/30">
                            <MapPin className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                          </div>
                          <span className="text-muted-foreground truncate">{member.location}</span>
                        </div>
                      )}
                    </div>

                    {/* View Details Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full group-hover:bg-blue-50 group-hover:text-blue-700 group-hover:border-blue-200 dark:group-hover:bg-blue-950/30 dark:group-hover:text-blue-400 transition-all"
                    >
                      <Eye className="h-3.5 w-3.5 mr-2" />
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-0 shadow-md">
              <CardContent className="p-0">
                <div className="divide-y">
                  {filteredMembers.map(member => (
                    <div
                      key={member.id}
                      className="p-5 hover:bg-muted/50 transition-colors cursor-pointer group"
                      onClick={() => handleViewDetails(member)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <EmployeeImage
                            name={member.name}
                            photoUrl={member.photoUrl}
                            size="md"
                          />
                          {member.status === 'active' && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-background" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4">
                          <div>
                            <p className="font-semibold truncate group-hover:text-blue-600 transition-colors">{member.name}</p>
                            <Badge
                              variant="outline"
                              className={cn("text-xs mt-1.5", getStatusColor(member.status))}
                            >
                              <div className={cn(
                                "w-1.5 h-1.5 rounded-full mr-1.5",
                                member.status === 'active' ? "bg-emerald-500" : "bg-slate-400"
                              )} />
                              {member.status}
                            </Badge>
                          </div>
                          <div className="text-sm">
                            <p className="text-muted-foreground text-xs font-medium mb-1">Role</p>
                            <p className="truncate">{member.role}</p>
                          </div>
                          <div className="text-sm">
                            <p className="text-muted-foreground text-xs font-medium mb-1">Department</p>
                            <p className="truncate">{member.department}</p>
                          </div>
                          <div className="text-sm">
                            <p className="text-muted-foreground text-xs font-medium mb-1">Email</p>
                            <p className="truncate text-xs">{member.email}</p>
                          </div>
                          <div className="text-sm">
                            <p className="text-muted-foreground text-xs font-medium mb-1">Location</p>
                            <p className="truncate">{member.location || 'N/A'}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="group-hover:bg-blue-50 group-hover:text-blue-700 dark:group-hover:bg-blue-950/30">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Details Dialog */}
      <TeamMemberDetailsDialog
        member={selectedMember}
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        onMemberUpdate={handleMemberUpdate}
        getStatusColor={getStatusColor}
        formatDate={formatDate}
      />
    </div>
  );
};
